import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { 
  Users, Target, Phone, Calendar, TrendingUp, 
  UserPlus, BarChart3, PieChart, Activity, 
  Clock, CheckCircle, XCircle, AlertCircle,
  ArrowUp, ArrowDown, MoreHorizontal, Download,
  Filter, Search, RefreshCw
} from 'lucide-react';

const Dashboard = () => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('week');
  
  // Stats globales
  const [globalStats, setGlobalStats] = useState({
    totalLeads: 0,
    totalCalls: 0,
    conversionRate: 0,
    activeUsers: 0,
    leadsTrend: 0,
    callsTrend: 0,
    conversionTrend: 0
  });

  // Stats pour admin
  const [adminStats, setAdminStats] = useState({
    totalUsers: 0,
    totalCampaigns: 0,
    activeAgents: 0,
    recentLeads: [],
    topAgents: [],
    leadsByStatus: [
      { status: 'Nouveau', count: 0, color: '#3b82f6' },
      { status: 'Contacté', count: 0, color: '#f59e0b' },
      { status: 'Qualifié', count: 0, color: '#10b981' },
      { status: 'Converti', count: 0, color: '#8b5cf6' },
      { status: 'Perdu', count: 0, color: '#ef4444' }
    ],
    activityData: []
  });

  // Stats pour manager
  const [managerStats, setManagerStats] = useState({
    teamLeads: 0,
    teamCalls: 0,
    myLeads: 0,
    teamMembers: [],
    teamPerformance: [],
    recentTeamActivity: []
  });

  // Stats pour agent
  const [agentStats, setAgentStats] = useState({
    myLeads: 0,
    myCalls: 0,
    pendingTasks: 0,
    todayCalls: 0,
    conversionRate: 0,
    recentLeads: [],
    upcomingCalls: [],
    performance: {
      daily: 0,
      weekly: 0,
      monthly: 0
    }
  });

  useEffect(() => {
    loadUserAndData();
  }, [timeRange]);

  const loadUserAndData = async () => {
    try {
      setLoading(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
      
      if (!user) {
        setLoading(false);
        return;
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
      
      setProfile(profile);
      
      await loadDataByRole(user, profile);

    } catch (error) {
      console.error('Erreur chargement dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDataByRole = async (user, profile) => {
    const role = profile?.role || 'user';

    switch(role) {
      case 'super_admin':
      case 'admin':
        await loadAdminStats();
        break;
      case 'manager':
        await loadManagerStats(user.id);
        break;
      case 'agent':
        await loadAgentStats(user.id);
        break;
      default:
        await loadUserStats(user.id);
    }
  };

  const loadAdminStats = async () => {
    // Stats globales
    const { count: usersCount } = await supabase
      .from('profiles')
      .select('*', { count: 'exact', head: true });
    
    const { count: campaignsCount } = await supabase
      .from('campaigns')
      .select('*', { count: 'exact', head: true });
    
    const { count: agentsCount } = await supabase
      .from('profiles')
      .select('*', { count: 'exact', head: true })
      .eq('role', 'agent');
    
    const { count: leadsCount } = await supabase
      .from('leads')
      .select('*', { count: 'exact', head: true });
    
    const { count: convertedCount } = await supabase
      .from('leads')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'converti');

    // Stats par statut
    const statusCounts = await Promise.all(
      ['nouveau', 'contacté', 'qualifié', 'converti', 'perdu'].map(async (status) => {
        const { count } = await supabase
          .from('leads')
          .select('*', { count: 'exact', head: true })
          .eq('status', status);
        return count || 0;
      })
    );

    setAdminStats({
      totalUsers: usersCount || 0,
      totalCampaigns: campaignsCount || 0,
      activeAgents: agentsCount || 0,
      leadsByStatus: adminStats.leadsByStatus.map((item, index) => ({
        ...item,
        count: statusCounts[index]
      }))
    });

    setGlobalStats({
      totalLeads: leadsCount || 0,
      conversionRate: leadsCount > 0 ? Math.round((convertedCount / leadsCount) * 100) : 0,
      activeUsers: usersCount || 0,
      leadsTrend: 12,
      callsTrend: 8,
      conversionTrend: 5
    });

    // Activité récente
    const { data: recentLeads } = await supabase
      .from('leads')
      .select('*, assigned_to_profile:assigned_to(email, full_name)')
      .order('created_at', { ascending: false })
      .limit(10);
    
    setAdminStats(prev => ({ ...prev, recentLeads: recentLeads || [] }));
  };

  const loadManagerStats = async (managerId) => {
    const { data: agents } = await supabase
      .from('profiles')
      .select('id, email, full_name')
      .eq('manager_id', managerId)
      .eq('role', 'agent');
    
    if (agents && agents.length > 0) {
      const agentIds = agents.map(a => a.id);
      
      const { count: teamLeads } = await supabase
        .from('leads')
        .select('*', { count: 'exact', head: true })
        .in('assigned_to', agentIds);
      
      const { count: teamCalls } = await supabase
        .from('calls')
        .select('*', { count: 'exact', head: true })
        .in('agent_id', agentIds);

      const teamPerformance = await Promise.all(agents.map(async (agent) => {
        const { count: agentLeads } = await supabase
          .from('leads')
          .select('*', { count: 'exact', head: true })
          .eq('assigned_to', agent.id);
        
        const { count: agentCalls } = await supabase
          .from('calls')
          .select('*', { count: 'exact', head: true })
          .eq('agent_id', agent.id);
        
        return {
          ...agent,
          leads: agentLeads || 0,
          calls: agentCalls || 0,
          performance: Math.round((agentLeads / (teamLeads || 1)) * 100)
        };
      }));

      setManagerStats({
        teamLeads: teamLeads || 0,
        teamCalls: teamCalls || 0,
        teamMembers: agents,
        teamPerformance: teamPerformance.sort((a, b) => b.leads - a.leads)
      });
    }

    const { count: myLeads } = await supabase
      .from('leads')
      .select('*', { count: 'exact', head: true })
      .eq('assigned_to', managerId);

    setManagerStats(prev => ({ ...prev, myLeads: myLeads || 0 }));
  };

  const loadAgentStats = async (agentId) => {
    const today = new Date().toISOString().split('T')[0];
    const startOfWeek = new Date();
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
    const weekStart = startOfWeek.toISOString().split('T')[0];

    // Stats principales
    const { count: myLeads } = await supabase
      .from('leads')
      .select('*', { count: 'exact', head: true })
      .eq('assigned_to', agentId);
    
    const { count: myCalls } = await supabase
      .from('calls')
      .select('*', { count: 'exact', head: true })
      .eq('agent_id', agentId);
    
    const { count: todayCalls } = await supabase
      .from('calls')
      .select('*', { count: 'exact', head: true })
      .eq('agent_id', agentId)
      .gte('created_at', today);

    const { count: converted } = await supabase
      .from('leads')
      .select('*', { count: 'exact', head: true })
      .eq('assigned_to', agentId)
      .eq('status', 'converti');

    // Performance par période
    const { count: dailyLeads } = await supabase
      .from('leads')
      .select('*', { count: 'exact', head: true })
      .eq('assigned_to', agentId)
      .gte('created_at', today);

    const { count: weeklyLeads } = await supabase
      .from('leads')
      .select('*', { count: 'exact', head: true })
      .eq('assigned_to', agentId)
      .gte('created_at', weekStart);

    setAgentStats({
      myLeads: myLeads || 0,
      myCalls: myCalls || 0,
      todayCalls: todayCalls || 0,
      conversionRate: myLeads > 0 ? Math.round((converted / myLeads) * 100) : 0,
      performance: {
        daily: dailyLeads || 0,
        weekly: weeklyLeads || 0,
        monthly: myLeads || 0
      }
    });

    // Données récentes
    const { data: recentLeads } = await supabase
      .from('leads')
      .select('*')
      .eq('assigned_to', agentId)
      .order('created_at', { ascending: false })
      .limit(5);
    
    const { data: upcomingCalls } = await supabase
      .from('calls')
      .select('*, leads(*)')
      .eq('agent_id', agentId)
      .eq('status', 'planifié')
      .gte('scheduled_at', new Date().toISOString())
      .order('scheduled_at')
      .limit(5);

    setAgentStats(prev => ({
      ...prev,
      recentLeads: recentLeads || [],
      upcomingCalls: upcomingCalls || []
    }));
  };

  const loadUserStats = async (userId) => {
    const { count: myLeads } = await supabase
      .from('leads')
      .select('*', { count: 'exact', head: true })
      .eq('assigned_to', userId);
    
    setAgentStats({ myLeads: myLeads || 0 });
  };

  // Composants
  const StatCard = ({ title, value, icon: Icon, trend, subtitle, color = 'blue' }) => {
    const colors = {
      blue: { bg: '#eff6ff', text: '#1e40af', icon: '#3b82f6' },
      purple: { bg: '#f5f3ff', text: '#5b21b6', icon: '#8b5cf6' },
      green: { bg: '#f0fdf4', text: '#166534', icon: '#22c55e' },
      yellow: { bg: '#fefce8', text: '#854d0e', icon: '#eab308' },
      red: { bg: '#fef2f2', text: '#991b1b', icon: '#ef4444' }
    };
    const style = colors[color] || colors.blue;

    return (
      <div style={{
        backgroundColor: 'white',
        borderRadius: '1rem',
        padding: '1.5rem',
        boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05), 0 8px 10px -6px rgba(0,0,0,0.02)',
        border: '1px solid #f0f0f0'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <div style={{
            width: '3rem',
            height: '3rem',
            borderRadius: '0.75rem',
            backgroundColor: style.bg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Icon size={24} color={style.icon} />
          </div>
          {trend !== undefined && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem',
              padding: '0.25rem 0.5rem',
              borderRadius: '9999px',
              fontSize: '0.75rem',
              fontWeight: '500',
              backgroundColor: trend >= 0 ? '#f0fdf4' : '#fef2f2',
              color: trend >= 0 ? '#166534' : '#991b1b'
            }}>
              {trend >= 0 ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
              <span>{Math.abs(trend)}%</span>
            </div>
          )}
        </div>
        <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>{title}</p>
        <p style={{ fontSize: '1.875rem', fontWeight: 'bold', color: '#111827', marginBottom: '0.25rem' }}>{value}</p>
        {subtitle && <p style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{subtitle}</p>}
      </div>
    );
  };

  const ActivityItem = ({ lead }) => (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      padding: '0.75rem',
      borderRadius: '0.5rem',
      transition: 'background-color 0.2s',
      cursor: 'pointer'
    }}
    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
    >
      <div style={{
        width: '2.5rem',
        height: '2.5rem',
        borderRadius: '0.5rem',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '0.875rem',
        fontWeight: '600',
        color: 'white'
      }}>
        {lead.first_name?.[0]}{lead.last_name?.[0]}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
          <span style={{ fontWeight: '500', fontSize: '0.875rem' }}>
            {lead.first_name} {lead.last_name}
          </span>
          <span style={{
            padding: '0.125rem 0.5rem',
            borderRadius: '9999px',
            fontSize: '0.625rem',
            fontWeight: '500',
            backgroundColor: lead.status === 'nouveau' ? '#dbeafe' :
                           lead.status === 'contacté' ? '#fef3c7' :
                           lead.status === 'qualifié' ? '#dcfce7' :
                           lead.status === 'converti' ? '#f3e8ff' : '#f3f4f6',
            color: lead.status === 'nouveau' ? '#1e40af' :
                   lead.status === 'contacté' ? '#92400e' :
                   lead.status === 'qualifié' ? '#166534' :
                   lead.status === 'converti' ? '#6b21a8' : '#4b5563'
          }}>
            {lead.status}
          </span>
        </div>
        <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>
          {lead.email} · {new Date(lead.created_at).toLocaleDateString('fr-FR')}
        </p>
      </div>
    </div>
  );

  const CallItem = ({ call }) => (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      padding: '0.75rem',
      borderRadius: '0.5rem',
      border: '1px solid #f0f0f0',
      marginBottom: '0.5rem'
    }}>
      <div style={{
        width: '2.5rem',
        height: '2.5rem',
        borderRadius: '0.5rem',
        backgroundColor: '#fff3cd',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Phone size={16} color="#856404" />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
          <span style={{ fontWeight: '500', fontSize: '0.875rem' }}>
            {call.leads?.first_name} {call.leads?.last_name}
          </span>
          <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
            {new Date(call.scheduled_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
        <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>
          {call.leads?.phone || 'Pas de téléphone'}
        </p>
      </div>
    </div>
  );

  const AgentProgress = ({ agent }) => {
    const percentage = Math.min(100, (agent.leads / 50) * 100);
    return (
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
          <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>{agent.full_name || agent.email}</span>
          <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
            {agent.leads} leads · {agent.calls} appels
          </span>
        </div>
        <div style={{
          width: '100%',
          height: '0.5rem',
          backgroundColor: '#f3f4f6',
          borderRadius: '9999px',
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${percentage}%`,
            height: '100%',
            background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)',
            borderRadius: '9999px',
            transition: 'width 0.3s ease'
          }} />
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#f9fafb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <RefreshCw size={40} color="#2563eb" style={{ animation: 'spin 1s linear infinite', marginBottom: '1rem' }} />
          <p style={{ color: '#6b7280' }}>Chargement de votre tableau de bord...</p>
        </div>
      </div>
    );
  }

  const role = profile?.role || 'user';

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f9fafb',
      padding: '2rem'
    }}>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      <div style={{
        maxWidth: '1440px',
        margin: '0 auto'
      }}>
        {/* En-tête */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem'
        }}>
          <div>
            <h1 style={{
              fontSize: '2rem',
              fontWeight: '600',
              color: '#111827',
              marginBottom: '0.25rem'
            }}>
              Tableau de bord
            </h1>
            <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
              {new Date().toLocaleDateString('fr-FR', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>
          
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '0.5rem',
                border: '1px solid #e5e7eb',
                backgroundColor: 'white',
                fontSize: '0.875rem',
                color: '#111827',
                cursor: 'pointer'
              }}
            >
              <option value="day">Aujourd'hui</option>
              <option value="week">Cette semaine</option>
              <option value="month">Ce mois</option>
              <option value="year">Cette année</option>
            </select>
            
            <button style={{
              padding: '0.5rem 1rem',
              borderRadius: '0.5rem',
              border: '1px solid #e5e7eb',
              backgroundColor: 'white',
              fontSize: '0.875rem',
              color: '#111827',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <Download size={16} />
              Exporter
            </button>
          </div>
        </div>

        {/* DASHBOARD ADMIN */}
        {(role === 'super_admin' || role === 'admin') && (
          <>
            {/* Stats principales */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '1.5rem',
              marginBottom: '2rem'
            }}>
              <StatCard 
                title="Leads totaux" 
                value={globalStats.totalLeads.toLocaleString()} 
                icon={Target} 
                trend={globalStats.leadsTrend}
                subtitle="+128 cette semaine"
                color="blue"
              />
              <StatCard 
                title="Taux de conversion" 
                value={`${globalStats.conversionRate}%`} 
                icon={TrendingUp} 
                trend={globalStats.conversionTrend}
                subtitle="Objectif: 25%"
                color="green"
              />
              <StatCard 
                title="Utilisateurs actifs" 
                value={adminStats.activeAgents} 
                icon={Users} 
                trend={8}
                subtitle={`${adminStats.totalUsers} au total`}
                color="purple"
              />
              <StatCard 
                title="Campagnes" 
                value={adminStats.totalCampaigns} 
                icon={Calendar} 
                trend={-2}
                subtitle="3 en cours"
                color="yellow"
              />
            </div>

            {/* Graphiques et activités */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '1.5rem',
              marginBottom: '1.5rem'
            }}>
              {/* Distribution des leads */}
              <div style={{
                backgroundColor: 'white',
                borderRadius: '1rem',
                padding: '1.5rem',
                boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#111827' }}>Distribution des leads</h3>
                  <button style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                    <MoreHorizontal size={20} color="#9ca3af" />
                  </button>
                </div>
                
                {adminStats.leadsByStatus.map((item, index) => (
                  <div key={index} style={{ marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '0.875rem', color: '#4b5563' }}>{item.status}</span>
                      <span style={{ fontSize: '0.875rem', fontWeight: '500', color: '#111827' }}>{item.count}</span>
                    </div>
                    <div style={{
                      width: '100%',
                      height: '0.5rem',
                      backgroundColor: '#f3f4f6',
                      borderRadius: '9999px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${(item.count / (globalStats.totalLeads || 1)) * 100}%`,
                        height: '100%',
                        backgroundColor: item.color,
                        borderRadius: '9999px'
                      }} />
                    </div>
                  </div>
                ))}
              </div>

              {/* Activité récente */}
              <div style={{
                backgroundColor: 'white',
                borderRadius: '1rem',
                padding: '1.5rem',
                boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#111827' }}>Activité récente</h3>
                  <button style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                    <Filter size={20} color="#9ca3af" />
                  </button>
                </div>
                
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {adminStats.recentLeads?.map(lead => (
                    <ActivityItem key={lead.id} lead={lead} />
                  ))}
                  {(!adminStats.recentLeads || adminStats.recentLeads.length === 0) && (
                    <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem' }}>
                      Aucune activité récente
                    </p>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* DASHBOARD MANAGER */}
        {role === 'manager' && (
          <>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '1.5rem',
              marginBottom: '2rem'
            }}>
              <StatCard title="Leads équipe" value={managerStats.teamLeads} icon={Target} color="blue" />
              <StatCard title="Appels équipe" value={managerStats.teamCalls} icon={Phone} color="purple" />
              <StatCard title="Mes leads" value={managerStats.myLeads} icon={UserPlus} color="green" />
              <StatCard title="Agents" value={managerStats.teamMembers.length} icon={Users} color="yellow" />
            </div>

            {managerStats.teamPerformance.length > 0 && (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '1rem',
                padding: '1.5rem',
                boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05)'
              }}>
                <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1.5rem' }}>
                  Performance de l'équipe
                </h3>
                {managerStats.teamPerformance.map(agent => (
                  <AgentProgress key={agent.id} agent={agent} />
                ))}
              </div>
            )}
          </>
        )}

        {/* DASHBOARD AGENT */}
        {role === 'agent' && (
          <>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: '1.5rem',
              marginBottom: '2rem'
            }}>
              <StatCard title="Mes leads" value={agentStats.myLeads} icon={Target} color="blue" />
              <StatCard title="Mes appels" value={agentStats.myCalls} icon={Phone} color="purple" />
              <StatCard title="Aujourd'hui" value={agentStats.todayCalls} icon={Calendar} color="green" />
              <StatCard title="Conversion" value={`${agentStats.conversionRate}%`} icon={TrendingUp} color="yellow" />
            </div>

            {/* Performance */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: '1.5rem',
              marginBottom: '2rem'
            }}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '1rem',
                padding: '1.5rem',
                textAlign: 'center'
              }}>
                <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>Aujourd'hui</p>
                <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#111827' }}>{agentStats.performance.daily}</p>
              </div>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '1rem',
                padding: '1.5rem',
                textAlign: 'center'
              }}>
                <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>Cette semaine</p>
                <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#111827' }}>{agentStats.performance.weekly}</p>
              </div>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '1rem',
                padding: '1.5rem',
                textAlign: 'center'
              }}>
                <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>Ce mois</p>
                <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#111827' }}>{agentStats.performance.monthly}</p>
              </div>
            </div>

            {/* Prochains appels et derniers leads */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '1.5rem'
            }}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '1rem',
                padding: '1.5rem',
                boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05)'
              }}>
                <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>Prochains appels</h3>
                {agentStats.upcomingCalls?.map(call => (
                  <CallItem key={call.id} call={call} />
                ))}
                {(!agentStats.upcomingCalls || agentStats.upcomingCalls.length === 0) && (
                  <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem' }}>
                    Aucun appel planifié
                  </p>
                )}
              </div>

              <div style={{
                backgroundColor: 'white',
                borderRadius: '1rem',
                padding: '1.5rem',
                boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05)'
              }}>
                <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>Derniers leads</h3>
                {agentStats.recentLeads?.map(lead => (
                  <ActivityItem key={lead.id} lead={lead} />
                ))}
                {(!agentStats.recentLeads || agentStats.recentLeads.length === 0) && (
                  <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem' }}>
                    Aucun lead récent
                  </p>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
